import { FC, ComponentProps } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';

import { flattenConnectionNodes } from '../../../utils/relayUtils';
import { moreFromThisSeller } from '../../pdpMessages';
import { trackSellerPageClick } from '../../tracking/similarItemsTracking';
import { PdpSharedProductTileCarousel } from '../../shared/PdpSharedProductTileCarousel/PdpSharedProductTileCarousel';

import { PdpSharedSellerItemsCarousel_viewer$key } from './__generated__/PdpSharedSellerItemsCarousel_viewer.graphql';
import { PdpSharedSellerItemsCarousel_item$key } from './__generated__/PdpSharedSellerItemsCarousel_item.graphql';
import { PdpSharedSellerItemsCarousel_user$key } from './__generated__/PdpSharedSellerItemsCarousel_user.graphql';

const viewerFragment = graphql`
    fragment PdpSharedSellerItemsCarousel_viewer on Viewer
    @refetchable(queryName: "PdpSharedSellerItemsCarouselPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        sellerItems: moreLikeThisItem(
            first: $count
            after: $cursor
            seller: INCLUDE
            itemId: $itemId
        ) @connection(key: "PdpSharedSellerItemsCarousel_sellerItems", filters: []) {
            edges {
                node {
                    ...PdpSharedProductTileCarousel_items
                        @arguments(
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...PdpSharedProductTileCarousel_viewer @arguments(userId: $userId)
    }
`;

const itemFragment = graphql`
    fragment PdpSharedSellerItemsCarousel_item on Item {
        serviceId
        seller {
            serviceId
            isAccessible
        }
    }
`;

const userFragment = graphql`
    fragment PdpSharedSellerItemsCarousel_user on User {
        ...PdpSharedProductTileCarousel_user
    }
`;

export const PdpSharedSellerItemsCarousel: FC<{
    viewer: PdpSharedSellerItemsCarousel_viewer$key;
    item: PdpSharedSellerItemsCarousel_item$key;
    user?: PdpSharedSellerItemsCarousel_user$key | null;
    currency: string;
    classOverrides?: ComponentProps<typeof PdpSharedProductTileCarousel>['classOverrides'];
}> = ({ viewer: viewerRef, item: itemRef, user: userRef = null, currency, classOverrides }) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const items = flattenConnectionNodes(viewer?.sellerItems);

    const { serviceId: itemPk, seller } = item;
    const sellerPk = seller?.serviceId;
    const totalResults = viewer.sellerItems?.totalResults || 0;

    if (!items.length || !seller?.isAccessible) {
        return null;
    }

    return (
        <PdpSharedProductTileCarousel
            viewer={viewer}
            items={items}
            user={user}
            title={moreFromThisSeller}
            viewAllLink={`/more-from-seller/?itemPk=${itemPk}&sellerPk=${sellerPk}`}
            isViewAllLinkable={false}
            handleViewAllClick={trackSellerPageClick}
            currency={currency}
            trackingList="pdp more from seller"
            dataTn="pdp-more-from-this-seller-grid"
            totalResults={totalResults}
            loadNext={loadNext}
            classOverrides={classOverrides}
            carouselCapacity={10}
        />
    );
};
